// angular
import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services
import {
  SystemConfigPromiseService,
  SysFeatures,
  Rate,
  COLUMN_ORDER_NDF_BASE_TRACKING,
} from "shared/cubiq-services";
import { TranslateService } from "@ngx-translate/core";
import { ObjectUtils } from "@bfx/primeng/utils";
// interface-components models

import {
  ICAutocompleteGenericModel,
  ICMarkupModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
// order-entry models
import { OEInputModel } from "../../data/models/oe-input.model";
import { OEDataModel } from "../../data/models/oe-data.model";
import { OEDLoadingDataModel } from "../../data/models/oe-loading-data.model";

// order-entry enums
import { OENPanel } from "../../data/enums/oe-panel.enum";
import { OENInputControl } from "../../data/enums/oe-input-control.enum";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OENCommand } from "../../data/enums/oe-command.enum";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { OENOperationMode } from "../../data/enums/oe-operation-mode.enum";
import { ICDTransportModel } from "../../data-idc/coordination/icd-transport.model";
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";
import { ICDBaseModel } from "../../data-idc/controls/icd-base.model";
import { ATDLDatasetService } from "../atdl/atdl-dataset.service";
import { ATDLDataModel } from "../../data-atdl/model/atdl-data.model";
import { ATDLNControlType } from "../../data-atdl/enums/atdl-control-type.enum";
import { ICDAutocompleteModel } from "../../data-idc/controls/icd-autocomplete.model";
import { ICDTextFieldModel } from "../../data-idc/controls/icd-text-field.model";
import { ICDSpinnerModel } from "../../data-idc/controls/icd-spinner.model";
import { ICDRadioInputModel } from "../../data-idc/controls/icd-radio-input.model";
import { ICDCheckboxModel } from "../../data-idc/controls/icd-checkbox.model";
import { ICDClockModel } from "../../data-idc/controls/icd-clock.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { OERateDisplayModel } from "../../data/models/rate/oe-rate-display.model";
import { OERateDataModel } from "../../data/models/rate/oe-rate-data.model";
import { OELockingProfileTransportModel } from "../../data/models/oe-locking-profile.model";

import { LoggerService } from "@bfx/logger";
import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";

import { v4 as uuid } from "uuid";

const logger = LoggerService.for(__dirname);

///

export const PRIVATIZATION_LABEL: string = "-PRIVATE-";
export const RESTRICTED_LABEL: string = "Restricted";

/**
 * IMPORTANT: This service should NOT contain reference to any Order entry services.
 */
@Injectable()
export class OEUtilityService {
  private fwdEnabled: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private translate: TranslateService,
    private _objectUtils: ObjectUtils,
    private atdlDataset: ATDLDatasetService,
    private systemConfigService: SystemConfigPromiseService,
  ) {
    systemConfigService.getSystemConfig(SysFeatures.fwdEnabled).then((value) => {
      this.fwdEnabled = value.toUpperCase() == "Y";
    });
    this.initialize();
  }

  /* INITIALIZATION */
  private initialize() {}

  /**********************************************************************/
  /* TRANSFORMERS/CONVERSIONS Methods*/
  public createInterfaceLoadDataFromModel(
    model: OEDataModel,
    blockPropagation: boolean = true,
  ): OEDLoadingDataModel {
    let data: Array<OEInputModel> = new Array<OEInputModel>();
    let algoData: Array<OEInputModel> = null;
    if (model != null) {
      // CLIENT
      if (model.isModificationPrivatized) {
        let label: string =
          model.client != null
            ? model.client.clientName
            : model.isClientRestricted
              ? RESTRICTED_LABEL
              : PRIVATIZATION_LABEL;
        if (model.entryType === OENEntryType.BENCHMARK) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_CLIENT,
              OENInputControl.CLIENT,
              { label: label, clientBranch: model.clientBranch },
              true,
            ),
          );
        } else {
          data.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.CLIENT, label, true));
        }
      } else if (model.client != null) {
        data.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.CLIENT, model.client));
      }
      // ACCOUNT
      if (model.isModificationPrivatized) {
        let label: string =
          model.account != null
            ? model.account.name
            : model.isClientRestricted
              ? RESTRICTED_LABEL
              : PRIVATIZATION_LABEL;
        data.push(
          new OEInputModel(
            OENPanel.PANEL_CLIENT,
            OENInputControl.ACCOUNT,
            label,
            model.isModificationPrivatized,
          ),
        );
      } else if (model.account != null) {
        data.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.ACCOUNT, model.account));
      }
      // CONTACTS
      data.push(
        new OEInputModel(
          OENPanel.PANEL_CLIENT,
          OENInputControl.CONTACT,
          model.contacts ?? [],
          model.isModificationPrivatized,
        ),
      );

      // CLIENT TRADER
      if (model.isClientTraderPrivatized) {
        let label =
          model.clientTrader != null && model.clientTrader instanceof CubiqTS.UserImpl
            ? model.clientTrader.userName
            : model.clientTrader instanceof ICAutocompleteGenericModel
              ? model.clientTrader.label
              : model.isClientRestricted
                ? RESTRICTED_LABEL
                : PRIVATIZATION_LABEL;

        data.push(
          new OEInputModel(
            OENPanel.PANEL_CLIENT,
            OENInputControl.TRADER,
            label,
            model.isClientTraderPrivatized,
          ),
        );
      } else if (model.clientTrader != null) {
        data.push(
          new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.TRADER, model.clientTrader),
        );
      }
      // TABs
      if (model.clActiveTab != null && model.clActiveTab.length > 0)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_CLIENT,
            OENInputControl.CLIENT_ACTIVE_TAB,
            model.clActiveTab,
          ),
        );
      if (model.accActiveTab != null && model.accActiveTab.length > 0)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_CLIENT,
            OENInputControl.ACCOUNT_ACTIVE_TAB,
            model.accActiveTab,
          ),
        );
      // ORDER TYPE (null allowed)
      if (
        model.entryType === OENEntryType.SPOT ||
        model.entryType === OENEntryType.NDF ||
        model.entryType === OENEntryType.GAMMA
      )
        // && model.orderType != null
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_TYPE, model.orderType),
        );
      // ALGO TYPE
      if (
        model.algoType != null &&
        (model.entryType === OENEntryType.ALGO || model.entryType === OENEntryType.NDF_ALGO)
      ) {
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ALGO_TYPE, model.algoType),
        );
      }
      // CCY_PAIR
      if (model.ccyPair != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CCY_PAIR, model.ccyPair),
        );
      // CLIENT INTENT
      if (model.clientIntent != null)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_FINANCIALS,
            OENInputControl.CLIENT_INTENT,
            model.clientIntent,
          ),
        );
      // FIXED CCY
      if (model.fixedCcy != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY, model.fixedCcy),
        );
      // ORDER AMOUNT
      if (model.orderAmount != null)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_FINANCIALS,
            OENInputControl.ORDER_AMOUNT,
            model.orderAmount,
          ),
        );
      // REQUEST PRICE
      if (
        model.entryType === OENEntryType.SPOT ||
        model.entryType === OENEntryType.NDF ||
        model.entryType === OENEntryType.GAMMA
      ) {
        //  model.requestPrice != null
        let entryInputModel = new OEInputModel(
          OENPanel.PANEL_FINANCIALS,
          OENInputControl.REQUEST_PRICE,
          model.requestPrice,
        );
        //entryInputModel.skipOverwrite = override; // avoid overriding the request price on ccy pair change
        data.push(entryInputModel);
      }
      // TSL PIPS
      if (model.entryType === OENEntryType.SPOT) {
        let entryInputModel = new OEInputModel(
          OENPanel.PANEL_FINANCIALS,
          OENInputControl.TRAILING_PIPS,
          model.trailingPips,
        );
        data.push(entryInputModel);
      }
      // Trailing Trigger
      if (model.entryType === OENEntryType.SPOT) {
        let entryInputModel = new OEInputModel(
          OENPanel.PANEL_FINANCIALS,
          OENInputControl.TRAILING_TRIGGER,
          model.trailingTriggerProvided ? model.trailingTrigger : null,
        );
        data.push(entryInputModel);
      }
      // VALUE DATE
      if (
        model.valueDate != null &&
        (model.entryType === OENEntryType.SPOT ||
          model.entryType === OENEntryType.ALGO ||
          model.entryType === OENEntryType.NDF_ALGO ||
          model.entryType === OENEntryType.BENCHMARK ||
          model.entryType === OENEntryType.NDF)
      ) {
        // spot / fwd
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.VALUE_DATE, model.valueDate),
        );
      }
      // TRIGGER
      if (
        (model.entryType === OENEntryType.SPOT || model.entryType === OENEntryType.NDF) &&
        model.trigger != null
      )
        // spot only
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.TRIGGER, model.trigger),
        );
      // LIMIT PRICE
      if (
        model.limitPrice != null &&
        (model.entryType === OENEntryType.ALGO || model.entryType === OENEntryType.NDF_ALGO)
      ) {
        // algo only
        data.push(
          new OEInputModel(
            OENPanel.PANEL_FINANCIALS,
            OENInputControl.LIMIT_PRICE,
            model.limitPrice,
          ),
        );
      }
      if (model.goodFrom != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_FROM, model.goodFrom),
        );
      if (model.goodUntil != null) {
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, model.goodUntil),
        );
      } else if (
        blockPropagation === true &&
        (model.entryType === OENEntryType.ALGO || model.entryType === OENEntryType.NDF_ALGO)
      ) {
        // Algo modify/duplicate/copy with good until null
        let gtcModel: ICGoodFromUntilModel = new ICGoodFromUntilModel();
        gtcModel.marketValue = "GTC";
        gtcModel.dayOrder = gtcModel.isNow = false;
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, gtcModel),
        );
      }

      if (model.suspend != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.SUSPEND, model.suspend),
        );
      if (model.traderGroup != null)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_FINANCIALS,
            OENInputControl.TRADER_GROUP,
            model.traderGroup,
          ),
        );
      // MARKUP
      if (model.markup != null && model.markup instanceof ICMarkupModel) {
        data.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.MARKUP, model.markup),
        );
      }
      // CLIENT COMMENTS
      if (model.isModificationClientCommentsPrivatized) {
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.CLIENT_COMMENTS,
            PRIVATIZATION_LABEL,
          ),
        );
      } else if (model.clientComment != null && model.clientComment.value != null) {
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.CLIENT_COMMENTS,
            model.clientComment.value,
          ),
        );
      } else {
        data.push(new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.CLIENT_COMMENTS, ""));
      }
      // PRIVATE COMMENTS
      if (model.isModificationPrivateCommentsPrivatized) {
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.PRIVATE_COMMENTS,
            PRIVATIZATION_LABEL,
          ),
        );
      } else if (model.privateComments != null) {
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.PRIVATE_COMMENTS,
            model.privateComments,
          ),
        );
      } else {
        data.push(new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.PRIVATE_COMMENTS, ""));
      }
      // HOLD STP
      if (model.holdSTP != null)
        data.push(new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.HOLD_STP, model.holdSTP));
      // AUTO FORWARD PRICE
      if (model != null) {
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.AUTO_FORWARD_PRICING,
            model.autoForwardPrice,
          ),
        );
      }
      // PRIVATE ORDER
      if (model.isModificationPrivatized && model.privateOrder) {
        // UI Privatization
        data.push(
          new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.PRIVATE_ORDER, PRIVATIZATION_LABEL),
        );
      } else if (model.privateOrder != null) {
        data.push(
          new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.PRIVATE_ORDER, model.privateOrder),
        );
      }
      // EXECUTION MODE
      if (model.entryType !== OENEntryType.ALGO && model.executionMode != null)
        data.push(
          new OEInputModel(
            OENPanel.PANEL_MORE,
            OENInputControl.EXECUTION_MODE,
            model.executionMode,
          ),
        );
      // PARTIAL FILL
      if (model.allowPartialFills != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.PARTIAL_FILL, {
            client: model.client,
            orderType: model.orderType,
            value: model.allowPartialFills,
          }),
        );

      // STP Aggregation allowed Flag
      if (model.stpAggregationFlag != null)
        data.push(
          new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.STP_AGG, model.stpAggregationFlag),
        );

      if (model.entryType === OENEntryType.NDF || model.entryType === OENEntryType.BENCHMARK) {
        if (model.fixingSource != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.FIXING_SOURCE,
              model.fixingSource,
            ),
          );
        }
        if (model.fixingCode != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.FIXING_SOURCE,
              model.fixingCode,
            ),
          );
        }
        if (model.fixingDate != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.FIXING_DATE,
              model.fixingDate,
            ),
          );
        }
        if (model.fixingTime != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.FIXING_TIME,
              model.fixingTime,
            ),
          );
        }
        if (model.fixingTimezone != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.FIXING_TIMEZONE,
              model.fixingTimezone,
            ),
          );
        }
      }

      if (model.entryType === OENEntryType.BENCHMARK) {
        if (Array.isArray(model.allocations) && model.allocations.length > 0) {
          // loading unbulks.
          data.push(
            new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ALLOCATIONS, {
              allocations: [...model.allocations, ...model.farAllocations],
              farAllocations: [],
              bulked: false,
              bulking: model.bulking,
            }),
          );
        }
      }

      if (model.oboClient != null) {
        data.push(
          new OEInputModel(OENPanel.PANEL_OBO, OENInputControl.OBO_CLIENT, model.oboClient),
        );
      }

      if (model.oboAccount != null) {
        data.push(
          new OEInputModel(OENPanel.PANEL_OBO, OENInputControl.OBO_ACCOUNT, model.oboAccount),
        );
      }

      // Gamma
      if (model.entryType === OENEntryType.GAMMA) {
        if (model.delta != null) {
          data.push(
            new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.DELTA, model.delta),
          );
        }

        if (model.rungsAbove != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.RUNGS_ABOVE,
              model.rungsAbove,
            ),
          );
        }

        if (model.rungsBelow != null) {
          data.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.RUNGS_BELOW,
              model.rungsBelow,
            ),
          );
        }
      }

      // STRATEGY PARAMETERS
      if (
        (model.entryType === OENEntryType.ALGO || model.entryType === OENEntryType.NDF_ALGO) &&
        model.algoParameters instanceof Array &&
        model.algoParameters.length > 0 &&
        model.algoType instanceof ICAutocompleteGenericModel
      ) {
        let paramsWithValues = new Array<string>();
        algoData = new Array<OEInputModel>();
        for (let parameter of model.algoParameters) {
          if (
            parameter instanceof ATDLParameterDataModel &&
            typeof parameter.icdId === "string" &&
            ((!(parameter.value instanceof ICDBaseModel) && parameter.value != null) ||
              (parameter.value instanceof ICDBaseModel && parameter.value.value != null))
          ) {
            paramsWithValues.push(parameter.refName);
            algoData.push(
              new OEInputModel(
                OENPanel.PANEL_ALGO,
                OENInputControl._ALGO_,
                new ICDTransportModel(parameter.icdId, parameter.value),
              ),
            );
          }
        }
        // logic for dealing with NULL parameters
        let algoModel = this.atdlDataset.get(model.algoType.value);
        if (
          algoModel instanceof ATDLDataModel &&
          algoModel.parameters instanceof Array &&
          algoModel.parameters.length > 0
        ) {
          for (let modelParameter of algoModel.parameters) {
            if (
              modelParameter instanceof ATDLParameterDataModel &&
              !paramsWithValues.includes(modelParameter.refName) &&
              modelParameter.constValue == null
            ) {
              let value = this.deviseValueForNullStrategyParameter(modelParameter);
              logger.out.info(
                `oeutil-s - NULL Value parameter for [${modelParameter.icdId}]: value = `,
                value,
              );
              if (value != null) {
                algoData.push(
                  new OEInputModel(
                    OENPanel.PANEL_ALGO,
                    OENInputControl._ALGO_,
                    new ICDTransportModel(modelParameter.icdId, value),
                  ),
                );
              }
            }
          }
        }
      }

      // custom fields
      let customValues = model.customValues;
      if (!customValues) {
        customValues = [];
      }
      data.push(
        new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.CUSTOM_FIELDS, {
          customValues,
          hasStrategyFill: model.hasStrategyFill,
        }),
      );
    }
    return new OEDLoadingDataModel(model.refId, model.entryType, blockPropagation, data, algoData);
  }

  public createInterfaceLoadDataForAlgoParameters(model: OEDataModel): OEDLoadingDataModel {
    let algoData = new Array<OEInputModel>();

    if (
      (model.entryType === OENEntryType.ALGO || model.entryType === OENEntryType.NDF_ALGO) &&
      model.algoParameters instanceof Array &&
      model.algoParameters.length > 0 &&
      model.algoType instanceof ICAutocompleteGenericModel
    ) {
      let paramsWithValues = new Array<string>();
      for (let parameter of model.algoParameters) {
        if (
          parameter instanceof ATDLParameterDataModel &&
          typeof parameter.icdId === "string" &&
          ((!(parameter.value instanceof ICDBaseModel) && parameter.value != null) ||
            (parameter.value instanceof ICDBaseModel && parameter.value.value != null))
        ) {
          paramsWithValues.push(parameter.refName);
          algoData.push(
            new OEInputModel(
              OENPanel.PANEL_ALGO,
              OENInputControl._ALGO_,
              new ICDTransportModel(parameter.icdId, parameter.value),
            ),
          );
        }
      }
      // logic for dealing with NULL parameters
      let algoModel = this.atdlDataset.get(model.algoType.value);
      if (
        algoModel instanceof ATDLDataModel &&
        algoModel.parameters instanceof Array &&
        algoModel.parameters.length > 0
      ) {
        for (let modelParameter of algoModel.parameters) {
          if (
            modelParameter instanceof ATDLParameterDataModel &&
            !paramsWithValues.includes(modelParameter.refName) &&
            modelParameter.constValue == null
          ) {
            let value = this.deviseValueForNullStrategyParameter(modelParameter);
            logger.out.info(`NULL Value parameter for [${modelParameter.icdId}]: value = `, value);
            if (value != null) {
              algoData.push(
                new OEInputModel(
                  OENPanel.PANEL_ALGO,
                  OENInputControl._ALGO_,
                  new ICDTransportModel(modelParameter.icdId, value),
                ),
              );
            }
          }
        }
      }
    }

    return new OEDLoadingDataModel(model.refId, model.entryType, true, [], algoData);
  }

  private deviseValueForNullStrategyParameter(modelParameter: ATDLParameterDataModel): any {
    if (modelParameter instanceof ATDLParameterDataModel) {
      let valueModel: ICDBaseModel = null;
      switch (modelParameter.icdType) {
        case ATDLNControlType.DROPDOWN_LIST:
          valueModel = new ICDAutocompleteModel();
          break;
        case ATDLNControlType.TEXTFIELD:
          valueModel = new ICDTextFieldModel();
          break;
        case ATDLNControlType.SINGLE_SPINNER:
          valueModel = new ICDSpinnerModel();
          break;
        case ATDLNControlType.CHECKBOX:
          valueModel = new ICDCheckboxModel();
          break;
        case ATDLNControlType.RADIO_LIST:
          valueModel = new ICDRadioInputModel();
          break;
        case ATDLNControlType.CLOCK:
          valueModel = new ICDClockModel();
          break;
        default:
          logger.out.error(
            `ERROR: _oeReverseAdapter.assignParameterModelFromString(): Unsupported control type`,
          );
          break;
      }

      if (valueModel != null) {
        let auxModel: ICDAuxCheckboxModel = new ICDAuxCheckboxModel();
        if (modelParameter.hasAuxiliaryCheckbox) {
          auxModel.enabled = true;
          auxModel.mode = ICDNAuxMode.OPTIONAL;
          auxModel.value = false;
        } else {
          auxModel.enabled = false;
          auxModel.mode = ICDNAuxMode.DISABLED;
          auxModel.value = false;
        }

        valueModel.auxModel = auxModel;
        valueModel.value = null;
        valueModel.disabled = false;
      }

      return valueModel;
    }
  }

  public convertRequestOrderTypeToOrderType(
    requestOrderType: CubiqTS.RequestOrderType,
  ): CubiqTS.OrderType {
    switch (requestOrderType) {
      case CubiqTS.RequestOrderType.CALL:
        return CubiqTS.OrderType.LIMIT_CALL;
      case CubiqTS.RequestOrderType.TP:
        return CubiqTS.OrderType.LIMIT_TP;
      case null:
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
        return CubiqTS.OrderType.LIMIT_SL;
      case CubiqTS.RequestOrderType.BMARK:
        return CubiqTS.OrderType.BENCHMARK;
      case CubiqTS.RequestOrderType.ALGO:
        return CubiqTS.OrderType.ALGO;
      case CubiqTS.RequestOrderType.MARKET:
        return CubiqTS.OrderType.MARKET;
      default:
        return null;
    }
  }

  public createAutocompleteForOrderType(
    orderType: CubiqTS.RequestOrderType,
  ): ICAutocompleteGenericModel {
    let title: string = this.translate.instant(
      "RequestOrderType." + CubiqTS.RequestOrderType[orderType],
    );
    return new ICAutocompleteGenericModel(orderType, title);
  }

  public convertStringToOrderTypeAutocompleteModel(
    orderTypeString: string,
  ): ICAutocompleteGenericModel {
    if (orderTypeString != null) {
      let title = this.translate.instant("RequestOrderType." + orderTypeString.toUpperCase());
      switch (orderTypeString.toUpperCase()) {
        case "CALL":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.CALL, title);
        case "TP":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TP, title);
        case "SL_ASK":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.SL_ASK, title);
        case "SL_BID":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.SL_BID, title);
        case "SL_ATRATE":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.SL_ATRATE, title);
        case "TSL":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TSL, title);
        case "TSL_ASK":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TSL_ASK, title);
        case "TSL_BID":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.TSL_BID, title);
        case "BMARK":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.BMARK, title);
        case "ALGO":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.ALGO, title);
        case "MARKET":
          return new ICAutocompleteGenericModel(CubiqTS.RequestOrderType.MARKET, title);
        default:
          return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Get ful LHS of an order ticket title bar - will contain the operation
   * @param entryType
   * @param operationMode
   * @returns
   */
  public determineScreenTitle(entryType: OENEntryType, operationMode?: OENOperationMode): string {
    let title: string;
    switch (operationMode) {
      case OENOperationMode.MODIFY:
      case OENOperationMode.MODIFY_INFO:
        title = this.translate.instant("order_entry.modify.title");
        break;
      case OENOperationMode.AMEND_MARKUP:
        title = this.translate.instant("order_entry.amend_markup.title");
        break;
      case OENOperationMode.EDIT:
        title = this.translate.instant("order_entry.edit.title");
        break;
      case OENOperationMode.MODIFY_SCRATCHPAD:
        title = this.translate.instant("order_entry.modify_scratchpad.title");
        break;
      case OENOperationMode.MONITOR_EXECUTION:
        const type = this.getTitleTypeFromEntryType(entryType);
        title = this.translate.instant("order_entry.monitor_execution.title", [type]);
        break;
      case OENOperationMode.NEW:
      case OENOperationMode.DUPLICATE:
      default:
        title = this.getTitleTypeFromEntryType(entryType);
        break;
    }
    return title;
  }

  /**
   * Convert an OEEntryType to a string e.g. "Spot/Fwd", "Algo" for use in title
   * @param entryType
   * @returns
   */
  private getTitleTypeFromEntryType(entryType: OENEntryType): string {
    let retTitle: string;
    switch (entryType) {
      case OENEntryType.SPOT:
        if (this.fwdEnabled) {
          retTitle = this.translate.instant("order_entry.spotFwd.title");
        } else {
          retTitle = this.translate.instant("order_entry.spot.title");
        }
        break;
      case OENEntryType.NDF_ALGO:
        retTitle = this.translate.instant("order_entry.ndfAlgo.title");
        break;
      case OENEntryType.NDF:
        retTitle = this.translate.instant("order_entry.ndf.title");
        break;
      case OENEntryType.ALGO:
        retTitle = this.translate.instant("order_entry.algo.title");
        break;
      case OENEntryType.BENCHMARK:
        retTitle = this.translate.instant("order_entry.bmark.title");
        break;
      case OENEntryType.GAMMA:
        retTitle = this.translate.instant("order_entry.gamma.title");
        break;
      default:
        logger.out.warn(`Unknown entry type ${entryType}`);
        retTitle = "<Unknown>";
        break;
    }
    return retTitle;
  }

  /**********************************************************************/
  /* GENERAL HELPER Methods*/

  public isTSL(requestOrderType): boolean {
    switch (requestOrderType) {
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
        return true;
      default:
        return false;
    }
  }

  public equals(object1: any, object2: any): boolean {
    return ObjectUtils.equals(object1, object2);
  }

  public compareObjects(object1: any, object2: any): boolean {
    return ObjectUtils.equals(object1, object2);
  }

  public compareDates(object1: Date, object2: Date): boolean {
    if ((object1 == null && object2 != null) || (object1 != null && object2 == null)) {
      return false;
    }
    if (object1 && object2) {
      if (object1.getTime() != object2.getTime()) {
        return false;
      }
    }
    return true;
  }

  public transformDisplayStateToCmd(displayState: OENDisplayState): OENCommand {
    let result: OENCommand = null;
    switch (displayState) {
      case OENDisplayState.SHOW_ORDER_ENTRY:
        result = OENCommand.STATE_ORDER_ENTRY;
        break;
      case OENDisplayState.SHOW_SUCCESS:
        result = OENCommand.STATE_ORDER_SUCCESS;
        break;
      case OENDisplayState.SHOW_MONITOR_EXECUTION:
        result = OENCommand.STATE_MONITOR_EXECUTION;
        break;
      case OENDisplayState.SHOW_REJECTED:
        result = OENCommand.STATE_ORDER_REJECTED;
        break;
      case OENDisplayState.SHOW_APPROVAL_ALLOWED:
        result = OENCommand.STATE_ORDER_APPROVAL_ALLOWED;
        break;
      case OENDisplayState.SHOW_APPROVAL_INTERNAL:
        result = OENCommand.STATE_ORDER_APPROVAL_INTERNAL;
        break;
      case OENDisplayState.SHOW_APPROVAL_EXTERNAL:
        result = OENCommand.STATE_ORDER_APPROVAL_EXTERNAL;
        break;
      case OENDisplayState.SHOW_ACCEPTANCE_ALLOWED:
        result = OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED;
        break;
      case OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL:
        result = OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL;
        break;
      case OENDisplayState.SHOW_PROCESSING:
        result = OENCommand.STATE_ORDER_PROCESSING;
        break;
      case OENDisplayState.SHOW_ACCEPTANCE_CALL:
        result = OENCommand.STATE_ORDER_ACCEPTANCE_CALL;
        break;
      case OENDisplayState.SHOW_UNLOCKED:
        result = OENCommand.STATE_ORDER_UNLOCKED;
        break;
      case OENDisplayState.SHOW_LOST_HIDDEN:
        result = OENCommand.STATE_ORDER_LOST_HIDDEN;
        break;
      case OENDisplayState.SHOW_EXPIRY_PENDING:
        result = OENCommand.STATE_ORDER_EXPIRY_PENDING;
        break;
    }
    return result;
  }

  public transformCmdToDisplayState(cmd: OENCommand): OENDisplayState {
    let result: OENDisplayState = null;
    switch (cmd) {
      case OENCommand.STATE_ORDER_ENTRY:
      case OENCommand.STATE_ORDER_IN_MODIFICATION:
        result = OENDisplayState.SHOW_ORDER_ENTRY;
        break;
      case OENCommand.STATE_ORDER_SUCCESS:
        result = OENDisplayState.SHOW_SUCCESS;
        break;
      case OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL:
      case OENCommand.STATE_MONITOR_EXECUTION:
        result = OENDisplayState.SHOW_MONITOR_EXECUTION;
        break;
      case OENCommand.STATE_ORDER_REJECTED:
      case OENCommand.STATE_ORDER_REJECTED_HCO_ERROR:
      case OENCommand.STATE_ORDER_INT_ERROR:
        result = OENDisplayState.SHOW_REJECTED;
        break;
      case OENCommand.STATE_ORDER_APPROVAL_ALLOWED:
        result = OENDisplayState.SHOW_APPROVAL_ALLOWED;
        break;
      case OENCommand.STATE_ORDER_APPROVAL_INTERNAL:
        result = OENDisplayState.SHOW_APPROVAL_INTERNAL;
        break;
      case OENCommand.STATE_ORDER_APPROVAL_EXTERNAL:
        result = OENDisplayState.SHOW_APPROVAL_EXTERNAL;
        break;
      case OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED:
        result = OENDisplayState.SHOW_ACCEPTANCE_ALLOWED;
        break;
      case OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL:
        result = OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL;
        break;
      case OENCommand.STATE_ORDER_PROCESSING:
        result = OENDisplayState.SHOW_PROCESSING;
        break;
      case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
      case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
        result = OENDisplayState.SHOW_TOLERANCE;
        break;
      case OENCommand.STATE_ORDER_ACCEPTANCE_CALL:
        result = OENDisplayState.SHOW_ACCEPTANCE_CALL;
        break;
      case OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE:
        result = OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE;
        break;
      case OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE:
        result = OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY;
        break;
      case OENCommand.STATE_ORDER_UNLOCKED:
        result = OENDisplayState.SHOW_UNLOCKED;
        break;
      case OENCommand.STATE_ORDER_LOST_HIDDEN:
        result = OENDisplayState.SHOW_LOST_HIDDEN;
        break;
      case OENCommand.STATE_ORDER_AMEND_MARKUP:
        result = OENDisplayState.SHOW_AMEND_MARKUP;
        break;
      case OENCommand.STATE_ORDER_EXPIRY_PENDING:
        result = OENDisplayState.SHOW_EXPIRY_PENDING;
        break;
      case OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP:
        result = OENDisplayState.SHOW_NEGATIVE_MARKUP_WARN;
        break;
      case OENCommand.BM_ZERO_AMOUNT_WARN:
        result = OENDisplayState.SHOW_BM_ZERO_AMOUNT_WARN;
        break;
      default:
        logger.out.error(
          `ERROR: _oeUtility.transformCmdToDisplayState(): Could not translate cmd '${cmd}' to state!`,
        );
        break;
    }
    return result;
  }

  /* IMPORTANT: order state transformation method may be incomplete or incorrect,
		there is no one-to-one correlation for most cases */
  public transformOrderStateToCmdState(orderState: CubiqTS.OrderState): OENCommand {
    let result: OENCommand = null;
    switch (orderState) {
      case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        result = OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE;
        break;
      case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
        result = OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE;
        break;
      case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        result = OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE;
        break;
      case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
        result = OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL;
        break;
      case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
        result = OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE;
        break;
      case CubiqTS.OrderState.IN_MODIFICATION:
        result = OENCommand.STATE_ORDER_IN_MODIFICATION;
        break;
      case CubiqTS.OrderState.ACTIVE:
        result = OENCommand.STATE_ORDER_SUCCESS;
        break;
      case CubiqTS.OrderState.REJECTED:
        result = OENCommand.STATE_ORDER_REJECTED;
        break;
      // terminal states
      case CubiqTS.OrderState.DELETED:
      case CubiqTS.OrderState.CANCELLED:
      case CubiqTS.OrderState.ABORTED:
      case CubiqTS.OrderState.DONE:
        result = OENCommand.STATE_ORDER_REJECTED;
        break;
      // Not handled states blow
      case CubiqTS.OrderState.NONE:
      case CubiqTS.OrderState.MANUAL_PRICING:
      case CubiqTS.OrderState.FILLED:
      case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.PRICED:
      case CubiqTS.OrderState.SAVED:
      case CubiqTS.OrderState.EXPIRED:
      case CubiqTS.OrderState.TRIGGERED:
      case CubiqTS.OrderState.CREDIT_CHECKING:
      case CubiqTS.OrderState.DI_NEEDED:
      case CubiqTS.OrderState.PRICING:
      case CubiqTS.OrderState.FILL_ERROR:
      case CubiqTS.OrderState.FILL_EXPIRED:
      case CubiqTS.OrderState.PENDING_CANCELLATION:
      case CubiqTS.OrderState.DI_APPROVED:
      case CubiqTS.OrderState.GAMMA_MID_PENDING:
      case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.ENTRYPRICING:
      case CubiqTS.OrderState.SUSPENDED:
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
      case CubiqTS.OrderState.REQUESTED:
      case CubiqTS.OrderState.TRADER_REJECTED:
      case CubiqTS.OrderState.SALES_REJECTED:
      case CubiqTS.OrderState.CLIENT_REJECTED:
      case CubiqTS.OrderState.ACCEPTED:
      case CubiqTS.OrderState.BOOK_PENDING:
      case CubiqTS.OrderState.CREDIT_PENDING:
      case CubiqTS.OrderState.PRICE_PENDING:
      case CubiqTS.OrderState.TIMED_OUT:
      case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
        logger.out.error(
          `ERROR: Invalid transformation for orderState ${CubiqTS.OrderState[orderState]}, displayState set to SHOW_REJECTED !`,
        );
        result = OENCommand.STATE_ORDER_REJECTED;
        break;
      default:
        logger.out.error("ERROR: Could not translate order state to display state!");
        break;
    }
    logger.out.debug(
      `DEBUG: _oeUtility.transformOrderStateToCmdState(): Translating orderState ${CubiqTS.OrderState[orderState]} => ${OENCommand[result]} displayState.`,
    );
    return result;
  }

  public determineOrderTypeFromRequestOrderType(
    requestOrderType: CubiqTS.RequestOrderType,
  ): CubiqTS.OrderType {
    switch (requestOrderType) {
      case CubiqTS.RequestOrderType.CALL:
        return CubiqTS.OrderType.LIMIT_CALL;
      case CubiqTS.RequestOrderType.TP:
        return CubiqTS.OrderType.LIMIT_TP;
      case null:
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
        return CubiqTS.OrderType.LIMIT_SL;
      case CubiqTS.RequestOrderType.BMARK:
        return CubiqTS.OrderType.BENCHMARK;
      case CubiqTS.RequestOrderType.ALGO:
        return CubiqTS.OrderType.ALGO;
      case CubiqTS.RequestOrderType.MARKET:
        return CubiqTS.OrderType.MARKET;
      default:
        return null;
    }
  }

  public generateRandomRefId(): string {
    return uuid();
  }

  public sanityCheckArgs(args: Array<any>) {
    let newArgs = [];
    if (args) {
      args.forEach((arg) => {
        if (arg) {
          if (arg.time) {
            newArgs.push(CubiqTS.moment.utc(arg.time).format("DD-MMM-YY HH:mm") + " UTC");
          } else {
            newArgs.push(arg);
          }
        } else if (arg === ``) {
          newArgs.push(``);
        } else {
          newArgs.push(`'${arg}'`);
        }
      });
    }
    return newArgs;
  }

  public removeClientTraderCommentFromManualComment(
    _manualComment: string,
    _clientTraderComment: string,
  ): string {
    let result = _manualComment;
    if (_clientTraderComment != null && _manualComment != null) {
      let index = _manualComment.indexOf("; " + _clientTraderComment);
      let indexPure = _manualComment.indexOf(_clientTraderComment); // just in case
      if (index >= 0) {
        result = _manualComment.replace("; " + _clientTraderComment, "");
      } else if (indexPure >= 0) {
        result = _manualComment.replace(_clientTraderComment, "");
      }
    }
    return result;
  }

  public checkLockingProfileTransportModel(
    lockingProfile: OELockingProfileTransportModel,
  ): boolean {
    return (
      lockingProfile instanceof OELockingProfileTransportModel &&
      Array.isArray(lockingProfile.profile) &&
      lockingProfile.profile.length > 0
    );
  }

  /**********************************************************************/
  /* MARKUP CONVERSION  */
  // Reverse adapter
  public getReverseDecsToPipsMultiplier(order: any, ccyPair: CubiqTS.CurrencyPairImpl): number {
    let pipPow = this.getPipsPrecisionForCcyPair(ccyPair, order.isInvertedOrderFg);
    let pipSize = Math.pow(10, pipPow);
    return pipSize;
  }

  /* 	private getPipsPrecisionForOrder(ccyPair: CubiqTS.CurrencyPairImpl, order: any): number {
        if (order.isInvertedOrderFg) {
            return ccyPair.spotInvertDecimalsQty;
        } else {
            return ccyPair.pipsQty;
        }
    } */
  // Forward adapter
  public getForwardDecsToPipsMultiplier(
    ccyPair: CubiqTS.CurrencyPairImpl,
    inverted: boolean,
  ): number {
    let pipPow = this.getPipsPrecisionForCcyPair(ccyPair, inverted);
    let pipSize = Math.pow(10, pipPow);
    return pipSize;
  }

  public getPipsPrecisionForCcyPair(ccyPair: CubiqTS.CurrencyPairImpl, inverted: boolean) {
    return inverted ? ccyPair.spotInvertDecimalsQty : ccyPair.pipsQty;
  }

  /**********************************************************************/
  /* ERROR HANDLER  */

  public handleErrors(errors: any[]): string {
    let message = "";
    let errorCounter = 1;
    errors
      .map((e) => JSON.parse(e))
      .forEach(
        (e) =>
          (message +=
            (errors.length > 1 ? errorCounter++ + ". " : "") +
            this.translate.instant(e["id"], this.sanityCheckArgs(e["args"])) +
            (errorCounter > 1 && errorCounter <= errors.length ? "<br/>" : "")),
      );
    return message;
  }

  public transformRateToOERateDataModel(
    subscrKey: string,
    rate: Rate,
    ccyPairModel: CubiqTS.CurrencyPairImpl,
  ): OERateDataModel {
    if (
      typeof subscrKey === "string" &&
      rate instanceof Rate &&
      ccyPairModel instanceof CubiqTS.CurrencyPairImpl
    ) {
      try {
        let invertedRate = rate.invert();
        let decimals: number = ccyPairModel.rateDecimalsQty;
        let precision: CubiqTS.Precision = ccyPairModel.spotPrecision;
        let additional: number =
          precision == CubiqTS.Precision.HALF || precision == CubiqTS.Precision.TENTH ? 1 : 0;

        let invertedDecimals: number = ccyPairModel.spotInvertDecimalsQty;
        let invertedPrecision: CubiqTS.Precision = ccyPairModel.spotInvertPrecision;
        let invertedAdditional: number =
          invertedPrecision == CubiqTS.Precision.HALF ||
          invertedPrecision == CubiqTS.Precision.TENTH
            ? 1
            : 0;

        /* DISPLAY MODELs */
        let rateDisplay = new OERateDisplayModel();
        let invertedRateDisplay = new OERateDisplayModel();
        // value
        rateDisplay.bidValue = new CubiqTS.BigNumber(rate.bidRate);
        rateDisplay.offerValue = new CubiqTS.BigNumber(rate.offerRate);
        invertedRateDisplay.bidValue = new CubiqTS.BigNumber(invertedRate.bidRate); //new CubiqTS.BigNumber(1).dividedBy(invertedRate.bidRate);
        invertedRateDisplay.offerValue = new CubiqTS.BigNumber(invertedRate.offerRate); //new CubiqTS.BigNumber(1).dividedBy(invertedRate.offerRate);
        // display
        rateDisplay.bidStr =
          rateDisplay.bidValue instanceof CubiqTS.BigNumber
            ? rateDisplay.bidValue.toFixed(decimals + additional)
            : new CubiqTS.BigNumber(0).toFixed(decimals + additional);
        rateDisplay.offerStr =
          rateDisplay.offerValue instanceof CubiqTS.BigNumber
            ? rateDisplay.offerValue.toFixed(decimals + additional)
            : new CubiqTS.BigNumber(0).toFixed(decimals + additional);
        invertedRateDisplay.bidStr =
          invertedRateDisplay.bidValue instanceof CubiqTS.BigNumber
            ? invertedRateDisplay.bidValue.toFixed(invertedDecimals + invertedAdditional)
            : new CubiqTS.BigNumber(0).toFixed(invertedDecimals + invertedAdditional);
        invertedRateDisplay.offerStr =
          invertedRateDisplay.offerValue instanceof CubiqTS.BigNumber
            ? invertedRateDisplay.offerValue.toFixed(invertedDecimals + invertedAdditional)
            : new CubiqTS.BigNumber(0).toFixed(invertedDecimals + invertedAdditional);
        // validity
        rateDisplay.invalid = invertedRateDisplay.invalid = !rate.tradable;

        /* DATA MODEL */
        let rateData = new OERateDataModel(subscrKey);
        // value
        rateData.rateValue = new CubiqTS.BigNumber(rate.bidRate).plus(rate.offerRate).dividedBy(2);
        rateData.invertedRateValue = new CubiqTS.BigNumber(invertedRate.bidRate)
          .plus(invertedRate.offerRate)
          .dividedBy(2);
        // display
        rateData.displayModel = rateDisplay;
        rateData.invertedDisplayModel = invertedRateDisplay;
        // ccyPair
        rateData.ccyPair = ccyPairModel.base + ccyPairModel.nonBase; //
        rateData.invertedCcyPair = ccyPairModel.nonBase + ccyPairModel.base;
        // rate
        rateData.rate = rate;

        return rateData;
      } catch (ex) {
        logger.out.error(`ERROR: oeRSM.transformRateToOERateDataModel(): Exception: ${ex}`);
        return null;
      }
    } else {
      logger.out.error(`ERROR: oeRSM.transformRateToOERateDataModel(): Invalid input parameter`);
      return null;
    }
  }
}
